import React, { useState, useContext } from 'react'
import ReactDOM from 'react-dom'
import { useMutation } from '@apollo/client'
import { DialogContext } from '../context/DialogContext.js'
import UserContext from '../context/UserContext.js'
import languages, { getLanguageAcronym } from '../languages.js'
import { ADD_MEETING_WITH_CONTENT_MUTATION } from '../query/gqlQueries.js'
import styles from './AddProjectDialog.module.scss'
import CloseIcon from '@mui/icons-material/Close'
import readXlsxFile from 'read-excel-file'
import { streamOllamaGenerate } from '../ollama.js'

const MODELS = ['llama3.2-vision:latest', 'qwen2.5:14b', 'llama3.1:8b', 'qwen2.5:7b', 'qwen2.5:3b']

// A tutor often writes a whole paradigm on one line — every form of a
// construction, space-separated. This is language-agnostic: it looks purely at
// the SHAPE of the line, not at any specific words. It fires on a short particle
// that repeats at a constant stride, whether leading or trailing:
//   RU  "у меня у тебя у вас у них …"      (leading "у")
//   FR  "à moi à toi à lui à nous"         (leading "à")
//   ES  "de mí de ti de él"                (leading "de")
// It CANNOT catch paradigms with no repeated token ("мой твой наш ваш") or
// suffix languages where the marker is glued on with no space (Korean/Turkish):
// those are left to the model + prompt. Returns null for ordinary lines so they
// pass through untouched.
//
// Splits `list` into groups that each begin at one of `positions` (a repeated
// token's indices) and tile the whole array at a constant stride.
function tileByPositions(list, positions) {
  return positions.map((start, k) => {
    const end = k + 1 < positions.length ? positions[k + 1] : list.length
    return list.slice(start, end).join(' ')
  })
}

// Indices where a short repeated marker sits, if the line is a paradigm keyed on
// `edge` ('lead' = marker starts each group, 'trail' = marker ends each group).
function paradigmPositions(tokens, edge) {
  const anchor = (edge === 'lead' ? tokens[0] : tokens[tokens.length - 1]).toLowerCase()
  if (anchor.length > 3) return null                 // particle, not a content word
  const at = tokens.reduce((acc, t, i) => (t.toLowerCase() === anchor ? [...acc, i] : acc), [])
  if (at.length < 3) return null
  const stride = at[1] - at[0]
  if (stride < 1 || stride > 4) return null
  if (!at.every((p, k) => k === 0 || p - at[k - 1] === stride)) return null
  if (edge === 'lead') {
    if (at[0] !== 0 || tokens.length - at[at.length - 1] !== stride) return null
    return at
  }
  // Trailing marker: convert to group *starts* so the groups still tile forwards.
  if (at[at.length - 1] !== tokens.length - 1) return null
  const starts = at.map((p) => p - (stride - 1))
  if (starts[0] !== 0) return null
  return starts
}

// Break one line into paradigm members, or return null. Two shapes, tried in
// order — both language-agnostic:
//   1. Explicit delimiters (comma / semicolon / newline / slash). Catches a
//      conjugation with a DIFFERENT word each person, which has no repeated
//      token to key on: "eu falo, tu falas, ele fala, nós falamos".
//   2. A short particle repeating at a constant stride (leading or trailing),
//      for delimiter-free lists: "у меня у тебя у вас …", "à moi à toi à lui".
function segmentForms(str) {
  const s = String(str || '').trim()
  if (!s) return null

  // 1. Delimited list. Require ≥3 pieces, each SHORT (a form, not a clause), so
  // an ordinary sentence with a comma or two is never shredded.
  const byDelim = s.split(/\s*[;,\n]\s*|\s+\/\s+/).map((x) => x.trim()).filter(Boolean)
  if (
    byDelim.length >= 3 &&
    byDelim.every((p) => p.split(/\s+/).length <= 4 && !/[?!]/.test(p))  // short forms, not clauses
  ) return byDelim

  // 2. Repeated-particle list.
  const tokens = s.split(/\s+/).filter(Boolean)
  if (tokens.length >= 4) {
    const pos = paradigmPositions(tokens, 'lead') || paradigmPositions(tokens, 'trail')
    if (pos) return tileByPositions(tokens, pos)
  }
  return null
}

function splitParadigm(source, target) {
  const pieces = segmentForms(source)
  if (!pieces) return null
  // Split the translation the same way; only pair it up if the counts match,
  // otherwise leave targets blank rather than mis-aligning them.
  let targetPieces = segmentForms(target)
  if (!targetPieces || targetPieces.length !== pieces.length) targetPieces = null
  return pieces.map((p, i) => ({ source: p, target: targetPieces ? targetPieces[i] : '' }))
}

// A line is a sentence if it contains a space OR ends with sentence
// punctuation. The punctuation check is what makes this work for Korean and
// Japanese, where a full sentence can be one space-free token ("아버지예요.")
// that the space-only rule would wrongly file as vocabulary.
const looksLikeSentence = (text) =>
  /\s/.test(String(text)) || /[.!?…。？！¿¡]$/.test(String(text).trim())

// Strip the chrome out of a pasted lesson chat (Preply / Telegram style):
// bullet markers, bare "10:26" timestamps, and the sender-name line that always
// sits directly above a timestamp. Real vocab lines survive, one per line — so a
// paradigm the tutor wrote across several lines stays several lines.
function cleanChatText(raw) {
  const lines = String(raw || '').split(/\r\n|\r|\n/)
  const strip = (l) => l.replace(/^\s*[*•·•-]\s*/, '').trim()
  const isBlank = (l) => strip(l) === ''
  const isTimestamp = (l) => /^\s*\d{1,2}:\d{2}\s*(am|pm)?\s*$/i.test(strip(l))

  const out = []
  for (let i = 0; i < lines.length; i++) {
    const l = strip(lines[i])
    if (l === '' || isTimestamp(lines[i])) continue
    // A sender-name line: the next non-blank line is a timestamp. Drop it.
    let j = i + 1
    while (j < lines.length && isBlank(lines[j])) j++
    if (j < lines.length && isTimestamp(lines[j])) continue
    out.push(l)
  }
  return out.join('\n')
}

// Pull clean, newline-preserving text out of a rich paste and throw the images
// away. Prefers the HTML flavour (so block breaks become real newlines even when
// the plain-text flavour is flattened), falls back to text/plain.
function extractPastedText(clipboardData) {
  const html = clipboardData?.getData?.('text/html')
  if (html) {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    doc.querySelectorAll('img, svg, style, script').forEach((n) => n.remove())
    doc.querySelectorAll('br').forEach((br) => br.replaceWith(doc.createTextNode('\n')))
    doc.querySelectorAll('p, div, li, tr, h1, h2, h3').forEach((el) => el.appendChild(doc.createTextNode('\n')))
    return (doc.body.textContent || '').replace(/ /g, ' ')
  }
  return clipboardData?.getData?.('text/plain') || ''
}

export default function AddMeetingDocumentDialog() {
  const { dialogStates, closeDialog } = useContext(DialogContext)
  const { username } = useContext(UserContext)

  const [files, setFiles] = useState([])
  const [pastedText, setPastedText] = useState('')
  const [showPaste, setShowPaste] = useState(false)
  const [status, setStatus] = useState('idle') // idle, analyzing, reviewing, saving, completed, error
  const [errorMessage, setErrorMessage] = useState('')
  const [model, setModel] = useState('llama3.2-vision:latest')
  const [targetLanguage, setTargetLanguage] = useState('Portuguese')
  const [meetingTitle, setMeetingTitle] = useState('')
  const [meetingType, setMeetingType] = useState('Language Class')
  const [meetingDate, setMeetingDate] = useState(new Date().toISOString().split('T')[0])
  const [previewData, setPreviewData] = useState(null)
  const [progressMsg, setProgressMsg] = useState('')

  const [addMeetingWithContent] = useMutation(ADD_MEETING_WITH_CONTENT_MUTATION)

  const isOpen = dialogStates['dialogAddMeetingDocument']

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setFiles(Array.from(e.target.files))
    }
  }

  const handleClose = () => {
    closeDialog('dialogAddMeetingDocument')
    setFiles([])
    setPastedText('')
    setShowPaste(false)
    setStatus('idle')
    setPreviewData(null)
    setErrorMessage('')
    setMeetingTitle('')
    setMeetingType('Language Class')
    setMeetingDate(new Date().toISOString().split('T')[0])
    setProgressMsg('')
  }

  // Intercept the paste so images are dropped and line breaks are preserved,
  // then insert the cleaned text at the cursor.
  const handlePaste = (e) => {
    const text = extractPastedText(e.clipboardData)
    if (!text) return // nothing useful; let the browser handle it
    e.preventDefault()
    const el = e.target
    const start = el.selectionStart ?? pastedText.length
    const end = el.selectionEnd ?? pastedText.length
    const merged = pastedText.slice(0, start) + text + pastedText.slice(end)
    setPastedText(merged.replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n'))
  }

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => resolve(reader.result.split(',')[1])
      reader.onerror = (error) => reject(error)
    })

  const fileToText = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsText(file)
      reader.onload = () => resolve(reader.result)
      reader.onerror = (error) => reject(error)
    })

  const xlsxToText = async (file) => {
    const rows = await readXlsxFile(file)
    return rows
      .map((row) => row.map((cell) => (cell == null ? '' : String(cell).trim())).filter(Boolean).join(' | '))
      .filter((line) => line.length > 0)
      .join('\n')
  }

  const handleAnalyze = async () => {
    if (files.length === 0 && !pastedText.trim()) return
    setStatus('analyzing')
    setErrorMessage('')
    setProgressMsg('Preparing files...')

    // Resolve the ISO-3 code for the language the user selected ("Korean" → "kor").
    // Fall back to 'eng' only if the lookup truly fails, but warn — the dropdown
    // should always produce a recognised name.
    const srcLang = getLanguageAcronym(targetLanguage) || 'eng'
    if (srcLang === 'eng' && targetLanguage !== 'English') {
      console.warn(`Could not resolve language code for "${targetLanguage}" — defaulting to 'eng'`)
    }

    const totalStartTime = performance.now()

    try {
      const base64Images = []
      let textContents = ''

      for (const file of files) {
        if (file.type.startsWith('image/')) {
          const b64 = await fileToBase64(file)
          base64Images.push(b64)
        } else if (file.type === 'text/plain' || file.name.endsWith('.txt')) {
          const txt = await fileToText(file)
          textContents += `\n--- Chat Log ---\n${txt}\n`
        } else if (file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls')) {
          const txt = await xlsxToText(file)
          textContents += `\n--- Spreadsheet (${file.name}) ---\n${txt}\n`
        }
      }

      // Pasted lesson chat — cleaned of names/timestamps/bullets, newlines kept.
      const cleanedPaste = cleanChatText(pastedText)
      if (cleanedPaste.trim()) {
        textContents += `\n--- Lesson Chat (pasted) ---\n${cleanedPaste}\n`
      }

      const prompt = `
You are an expert language teacher and meeting analyzer.
Analyze the provided material. The target language being learned or spoken is ${targetLanguage}.

CRITICAL INSTRUCTIONS:
1. Extract ALL vocabulary words and full sentences you see in the images or text.
2. Do NOT break sentences down into individual words. Only extract a word if it stands alone as vocabulary.
3. If an English translation is not visible, YOU MUST generate the English translation yourself. Never leave "targetText" or "translation" blank.
4. If a single line lists several parallel forms of ONE construction — a grammar paradigm (all the possessive/pronoun forms, a full verb conjugation, a set of case endings) in ANY language — output EACH form as its OWN separate entry with its OWN precise English translation. NEVER merge a paradigm into one run-on entry. This applies whatever the target language is: e.g. ${targetLanguage} paradigms should each be split form-by-form, not left as one long line.

Return ONLY a valid JSON object. Do not include markdown code blocks or any other text outside the JSON.

{
  "sentences": [
    { "sourceText": "Text in ${targetLanguage}", "targetText": "English translation" }
  ],
  "words": [
    { "word": "Text in ${targetLanguage}", "translation": "English translation" }
  ]
}
      `.trim()

      const serverUrl = process.env.REACT_APP_OLLAMA_URI || 'http://localhost:11434'

      let allSentences = []
      let allWords = []

      // Process each image sequentially (Independent from chat log to save time)
      for (let i = 0; i < base64Images.length; i++) {
        setProgressMsg(`Analyzing image ${i + 1} of ${base64Images.length}...`)

        const startTime = performance.now()
        const rawResponse = await streamOllamaGenerate({
          serverUrl,
          apiKey: process.env.REACT_APP_OLLAMA_KEY,
          body: {
            model,
            prompt,
            format: 'json',
            options: { temperature: 0.1 },
            images: [base64Images[i]]
          },
          onProgress: (acc) => {
            setProgressMsg(`Analyzing image ${i + 1} of ${base64Images.length}… (${acc.length} chars streamed)`)
          }
        })
        const elapsed = ((performance.now() - startTime) / 1000).toFixed(2)
        console.log(`[Timer] Image ${i + 1} analysis took ${elapsed} seconds`)
        let rawJson = rawResponse.replace(/```json/g, '').replace(/```/g, '').trim()

        try {
          const parsed = JSON.parse(rawJson)
          if (parsed.sentences) allSentences.push(...parsed.sentences)
          if (parsed.words) allWords.push(...parsed.words)
        } catch (e) {
          console.warn(`Failed to parse JSON for image ${i + 1}`, e, rawJson)
        }
      }

      // If a chat log was provided, process it separately
      if (textContents) {
        setProgressMsg('Analyzing chat log text...')

        const startTime = performance.now()
        const rawResponse = await streamOllamaGenerate({
          serverUrl,
          apiKey: process.env.REACT_APP_OLLAMA_KEY,
          body: {
            model,
            prompt: prompt + `\nHere is the text context:\n${textContents}`,
            format: 'json',
            options: { temperature: 0.1 }
          },
          onProgress: (acc) => {
            setProgressMsg(`Analyzing chat log text… (${acc.length} chars streamed)`)
          }
        })
        const elapsed = ((performance.now() - startTime) / 1000).toFixed(2)
        console.log(`[Timer] Chat log text analysis took ${elapsed} seconds`)

        let rawJson = rawResponse.replace(/```json/g, '').replace(/```/g, '').trim()

        try {
          const parsed = JSON.parse(rawJson)
          if (parsed.sentences) allSentences.push(...parsed.sentences)
          if (parsed.words) allWords.push(...parsed.words)
        } catch (e) {
          console.warn('Failed to parse JSON for text', e)
        }
      }

      // --- PROGRAMMATIC CLEANUP & ENFORCEMENT ---
      let finalSentences = []
      let finalWords = []
      
      // Combine all raw items to process them identically
      let rawItems = [
        ...allSentences.map(s => ({ source: s.sourceText, target: s.targetText })), 
        ...allWords.map(w => ({ source: w.word, target: w.translation }))
      ]

      rawItems.forEach(item => {
        if (!item.source) return
        
        let source = item.source.trim()
        let target = item.target?.trim() || ''

        // 1. Clean parentheses for translations e.g., "você bebe (you drink)"
        const parenMatch = source.match(/^(.*?)\s*\((.*?)\)$/)
        if (parenMatch) {
          source = parenMatch[1].trim()
          target = parenMatch[2].trim() || target // prioritize english inside parentheses
        }

        // 1b. Expand a paradigm line ("у меня у тебя у вас …") into its members,
        // each with its own translation, before anything else touches it.
        const paradigm = splitParadigm(source, target)
        if (paradigm) {
          paradigm.forEach(({ source: p, target: t }) => {
            if (!p) return
            const isSentence = looksLikeSentence(p)
            if (isSentence) finalSentences.push({ sourceText: p, targetText: t, sourceLang: srcLang, targetLang: 'eng' })
            else finalWords.push({ word: p, translation: t, lang: srcLang })
          })
          return
        }

        // 2. Split arrows or dashes for variations/translations e.g., "copo -> copinho" or "olmak — to be"
        const hasArrow = source.includes('->')
        const hasEmDash = source.includes(' — ')
        const hasEnDash = source.includes(' – ')
        const hasHyphen = source.includes(' - ')

        if (hasArrow || hasEmDash || hasEnDash || hasHyphen) {
           let separator = ' - '
           if (hasArrow) separator = '->'
           else if (hasEmDash) separator = ' — '
           else if (hasEnDash) separator = ' – '

           const parts = source.split(separator).map(p => p.trim())
           const tParts = target.includes(separator) ? target.split(separator).map(p => p.trim()) : [target, target]
           
           parts.forEach((p, idx) => {
             if (!p) return

             // If the split part is actually the translation (e.g. English) and we already have a target, skip it
             // Or if it's the second part and we didn't split the target, use the second part AS the target
             let finalTarget = tParts[idx] || target
             if (parts.length === 2 && idx === 0) {
               finalTarget = parts[1] // The right side of the dash is the translation
             } else if (parts.length === 2 && idx === 1) {
               return // Skip the right side since we used it as the target for the left side
             }

             const isSentence = looksLikeSentence(p)
             if (isSentence) finalSentences.push({ sourceText: p, targetText: finalTarget, sourceLang: srcLang, targetLang: 'eng' })
             else finalWords.push({ word: p, translation: finalTarget, lang: srcLang })
           })
           return
        }

        // 3. Strict Categorization: Single word vs Sentence
        const isSentence = looksLikeSentence(source)
        if (isSentence) {
          finalSentences.push({ sourceText: source, targetText: target, sourceLang: srcLang, targetLang: 'eng' })
        } else {
          finalWords.push({ word: source, translation: target, lang: srcLang })
        }
      })

      // 4. Deduplicate exact matches
      const uniqueSentences = Array.from(new Map(finalSentences.map(item => [item.sourceText.toLowerCase(), item])).values())
      const uniqueWords = Array.from(new Map(finalWords.map(item => [item.word.toLowerCase(), item])).values())

      // 5. Generate Tasks if Language Class
      let finalTasks = []
      if (meetingType === 'Language Class' && (uniqueWords.length > 0 || uniqueSentences.length > 0)) {
        setProgressMsg('Generating homework tasks...')
        
        // Chunk words to avoid overloading a single task
        const chunkedWords = []
        for (let i = 0; i < uniqueWords.length; i += 15) {
          chunkedWords.push(uniqueWords.slice(i, i + 15).map(w => w.word).join(', '))
        }
        
        let wordPromptStr = ''
        let taskInstruction = ''
        let jsonExampleTasks = []
        
        if (chunkedWords.length > 0) {
          if (chunkedWords.length === 1) {
            wordPromptStr = `Vocabulary Words: ${chunkedWords[0]}`
            taskInstruction = `Generate 2 to 3 practical homework tasks using the Vocabulary Words and Sentences.`
            jsonExampleTasks.push(`    { "title": "Practice verbs", "type": "Homework", "description": "Write sentences using the vocabulary...", "priority": "Medium" }`)
            jsonExampleTasks.push(`    { "title": "Fill in the blanks", "type": "Homework", "description": "Use the words to complete...", "priority": "Low" }`)
          } else {
            wordPromptStr = chunkedWords.map((chunk, idx) => `Word Batch ${idx + 1}: ${chunk}`).join('\n')
            taskInstruction = `CRITICAL: There are ${chunkedWords.length} Word Batches. YOU MUST GENERATE EXACTLY ${chunkedWords.length} vocabulary tasks (one for each Word Batch). Plus, generate 1 extra task for the Sentences if they exist. You must return exactly ${chunkedWords.length + (uniqueSentences.length > 0 ? 1 : 0)} tasks total. Do not skip any Word Batch. Do not mix words from different batches into a single task.`
            
            for (let i = 0; i < chunkedWords.length; i++) {
              jsonExampleTasks.push(`    { "title": "Practice verbs (Part ${i + 1})", "type": "Homework", "description": "Write sentences using verbs from Word Batch ${i + 1}...", "priority": "Medium" }`)
            }
          }
        }
        
        if (uniqueSentences.length > 0) {
           jsonExampleTasks.push(`    { "title": "Practice Sentences", "type": "Homework", "description": "Practice the provided sentences...", "priority": "Medium" }`)
        }
        
        const jsonExample = `{\n  "tasks": [\n${jsonExampleTasks.join(',\\n')}\n  ]\n}`
        
        const taskPrompt = `
You are an expert language teacher. Based on the following vocabulary and sentences learned in class today, generate practical homework tasks for the student.

${taskInstruction}
Each task should have a short generic action title, be of type "Homework", and have a detailed description using the specific words/sentences for that task.

${wordPromptStr}
Sentences: ${uniqueSentences.map(s => s.sourceText).join('. ')}

Return ONLY a valid JSON object. Do not include markdown code blocks or any other text outside the JSON.
Follow this EXACT structure with exactly ${jsonExampleTasks.length} tasks:

${jsonExample}
        `.trim()

        try {
          const startTime = performance.now()
          const rawResponse = await streamOllamaGenerate({
            serverUrl,
            apiKey: process.env.REACT_APP_OLLAMA_KEY,
            body: {
              model,
              prompt: taskPrompt,
              format: 'json',
              options: { temperature: 0.2 }
            },
            onProgress: (acc) => {
              setProgressMsg(`Generating homework tasks… (${acc.length} chars streamed)`)
            }
          })
          const elapsed = ((performance.now() - startTime) / 1000).toFixed(2)
          console.log(`[Timer] Task generation took ${elapsed} seconds`)
          let rawJson = rawResponse.replace(/```json/g, '').replace(/```/g, '').trim()
          const parsed = JSON.parse(rawJson)
          if (parsed.tasks && Array.isArray(parsed.tasks)) {
            finalTasks = parsed.tasks.slice(0, 3) // strictly 3 tasks
          }
        } catch (e) {
          console.warn('Failed to generate tasks', e)
        }
      }

      const finalData = {
        sentences: uniqueSentences,
        words: uniqueWords,
        tasks: finalTasks
      }

      const totalElapsed = ((performance.now() - totalStartTime) / 1000).toFixed(2)
      console.log('--- AI Analysis Complete & Cleaned ---')
      console.log(`[Timer] Total Analysis Time: ${totalElapsed} seconds`)
      console.log('Parsed Sentences:', finalData.sentences)
      console.log('Parsed Words:', finalData.words)
      console.log('Parsed Tasks:', finalData.tasks)

      setPreviewData(finalData)
      setStatus('reviewing')
    } catch (err) {
      console.error(err)
      setStatus('error')
      if (err.message.includes('Failed to fetch') || err.message.includes('Load failed')) {
        setErrorMessage('Connection to Ollama failed. The tunnel may be down or the request may have been dropped mid-stream. Try a smaller model, or check that the Mac Studio is reachable.')
      } else {
        setErrorMessage(err.message || 'Failed to analyze documents. Make sure Ollama is running.')
      }
    }
  }

  const handleSave = async () => {
    if (!previewData) return
    setStatus('saving')

    try {
      await addMeetingWithContent({
        variables: {
          input: {
            meeting: {
              title: meetingTitle || 'New Meeting',
              type: meetingType || 'Language Class',
              description: `Meeting generated from documents`,
              meetingDate: meetingDate || null,
              username: username
            },
            sentences: previewData.sentences || [],
            words: previewData.words || [],
            tasks: previewData.tasks || []
          }
        }
      })
      setStatus('completed')
      setTimeout(() => {
        handleClose()
      }, 2000)
    } catch (err) {
      console.error(err)
      setStatus('error')
      setErrorMessage(err.message)
    }
  }

  if (!isOpen) return null

  return ReactDOM.createPortal(
    <div className={styles.overlay} onClick={handleClose}>
      <div className={styles.dialog} onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
        <button className={styles.closeBtn} onClick={handleClose}>
          <CloseIcon />
        </button>

        <div className={styles.header}>
          <div className={styles.titleGroup}>
            <div className={styles.label}>AI Analysis</div>
            <h2 className={styles.title}>Meeting Documents</h2>
          </div>
        </div>

        <div className={styles.container}>
          <div style={{ display: 'flex', gap: '1.5rem' }}>
            <div className={styles.field} style={{ flex: 1 }}>
              <div className={styles.fieldLabel}>Meeting Title</div>
              <input
                type="text"
                className={styles.input}
                value={meetingTitle}
                onChange={(e) => setMeetingTitle(e.target.value)}
                placeholder="e.g. Portuguese Class"
              />
            </div>
            <div className={styles.field} style={{ flex: 1 }}>
              <div className={styles.fieldLabel}>Meeting Type</div>
              <select className={styles.input} value={meetingType} onChange={(e) => setMeetingType(e.target.value)}>
                <option value="Language Class">Language Class</option>
                <option value="Business Meeting">Business Meeting</option>
                <option value="Workshop">Workshop</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '1.5rem' }}>
            <div className={styles.field} style={{ flex: 1 }}>
              <div className={styles.fieldLabel}>Meeting Date</div>
              <input
                type="date"
                className={styles.input}
                value={meetingDate}
                onChange={(e) => setMeetingDate(e.target.value)}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '1.5rem' }}>
            <div className={styles.field} style={{ flex: 1 }}>
              <div className={styles.fieldLabel}>Target Language</div>
              <select className={styles.input} value={targetLanguage} onChange={(e) => setTargetLanguage(e.target.value)}>
                {Object.entries(languages).map(([key, value]) => (
                  <option key={key} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.field} style={{ flex: 1 }}>
              <div className={styles.fieldLabel}>Ollama Model</div>
              <select className={styles.input} value={model} onChange={(e) => setModel(e.target.value)}>
                {MODELS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className={styles.field}>
            <div className={styles.fieldLabel}>Documents (.png, .jpg, .txt, .xlsx)</div>
            <input type="file" multiple accept=".jpg,.jpeg,.png,.txt,.xlsx,.xls" onChange={handleFileChange} className={styles.input} style={{ cursor: 'pointer' }} />
            {files.length > 0 && (
              <div style={{ marginTop: '0.8rem', fontSize: '0.85rem', color: '#6ee7b7' }}>
                {files.length} file(s) selected: {files.map((f) => f.name).join(', ')}
              </div>
            )}
          </div>

          <div className={styles.field}>
            <button
              type="button"
              onClick={() => setShowPaste((s) => !s)}
              style={{
                background: 'transparent', border: '1px dashed #3a3a42', color: '#6ee7b7',
                borderRadius: '8px', padding: '0.6rem 0.9rem', cursor: 'pointer',
                fontSize: '0.85rem', width: '100%', textAlign: 'left'
              }}
            >
              {showPaste ? '▾' : '▸'} Paste lesson text directly {pastedText.trim() && !showPaste ? `(${cleanChatText(pastedText).split('\n').filter(Boolean).length} lines ready)` : ''}
            </button>

            {showPaste && (
              <>
                <textarea
                  value={pastedText}
                  onPaste={handlePaste}
                  onChange={(e) => setPastedText(e.target.value)}
                  placeholder={'Paste straight from your Preply / lesson chat (⌘V).\nImages are removed automatically and line breaks are kept.'}
                  spellCheck="false"
                  style={{
                    marginTop: '0.6rem', width: '100%', minHeight: '160px', resize: 'vertical',
                    background: '#0d0d0f', border: '1px solid #3a3a42', borderRadius: '8px',
                    color: '#e8e8ec', fontFamily: "'DM Mono', monospace", fontSize: '0.85rem',
                    lineHeight: 1.5, padding: '0.7rem', boxSizing: 'border-box', whiteSpace: 'pre-wrap'
                  }}
                />
                <div style={{ marginTop: '0.4rem', display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#94a3b8' }}>
                  <span>{cleanChatText(pastedText).split('\n').filter(Boolean).length} lines after cleanup (names, times & images stripped)</span>
                  {pastedText && <button type="button" onClick={() => setPastedText('')} style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: '0.75rem' }}>clear</button>}
                </div>
              </>
            )}
          </div>

          {status === 'analyzing' && (
            <div style={{ color: '#6ee7b7', fontSize: '0.9rem', marginTop: '1rem', fontStyle: 'italic', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
              <div>Sending to Mac Studio Ollama for analysis...</div>
              {progressMsg && <div style={{ fontWeight: 'bold' }}>{progressMsg}</div>}
            </div>
          )}

          {status === 'error' && <div style={{ color: '#ff4d4d', fontSize: '0.9rem', marginTop: '1rem' }}>{errorMessage}</div>}

          {status === 'completed' && (
            <div style={{ color: '#6ee7b7', fontSize: '0.9rem', marginTop: '1rem' }}>Successfully saved meeting and all extracted data to the database!</div>
          )}

          {status === 'reviewing' && previewData && (
            <div
              style={{
                marginTop: '1rem',
                padding: '1rem',
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '12px',
                maxHeight: '300px',
                overflowY: 'auto',
                border: '1px solid #3a3a42'
              }}>
              <h4 style={{ margin: '0 0 1rem 0', color: '#6ee7b7' }}>Preview Extracted Data</h4>

              <div style={{ marginBottom: '0.5rem', fontWeight: 'bold' }}>Sentences ({previewData.sentences?.length || 0})</div>
              {previewData.sentences?.map((s, i) => (
                <div key={i} style={{ fontSize: '0.85rem', marginBottom: '0.3rem' }}>
                  <span style={{ color: '#6ee7b7' }}>{s.sourceText}</span> → {s.targetText}
                </div>
              ))}

              <div style={{ marginBottom: '0.5rem', marginTop: '1rem', fontWeight: 'bold' }}>Words ({previewData.words?.length || 0})</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {previewData.words?.map((w, i) => (
                  <span key={i} style={{ fontSize: '0.8rem', background: '#222226', padding: '0.2rem 0.5rem', borderRadius: '4px' }}>
                    {w.word} = {w.translation}
                  </span>
                ))}
              </div>

              <div style={{ marginBottom: '0.5rem', marginTop: '1rem', fontWeight: 'bold' }}>Tasks ({previewData.tasks?.length || 0})</div>
              {previewData.tasks?.map((t, i) => (
                <div key={i} style={{ fontSize: '0.85rem', marginBottom: '0.3rem' }}>
                  <strong style={{ color: '#6ee7b7' }}>{t.title}</strong> ({t.type}): {t.description}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={styles.footer}>
          {status !== 'reviewing' && status !== 'saving' && status !== 'completed' && (
            <button className={styles.primaryBtn} onClick={handleAnalyze} disabled={(files.length === 0 && !pastedText.trim()) || status === 'analyzing'}>
              {status === 'analyzing' ? 'Analyzing...' : 'Analyze Documents'}
            </button>
          )}
          {status === 'reviewing' && (
            <button className={styles.primaryBtn} onClick={handleSave}>
              Save Meeting
            </button>
          )}
          {status === 'saving' && (
            <button className={styles.primaryBtn} disabled>
              Saving...
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}
